// @flow
import itemsAdded from 'app/common/redux/actions/feeds/itemsAdded';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { Thunk } from 'app/common/redux/types';
import { incrementCommentCount } from 'app/common/redux/actions/activityItem/updateCommentCount';
import {
  type BoardActivityItem,
  type BoardActivityItemType,
  BOARD_ACTIVITY_OBJECT_TYPES,
  PARENT_TO_FEED_TYPE,
} from 'app/common/redux/reducers/activityItems';

const PARENT_TO_CREATE_RESOURCE_MAP = {
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD]: 'BoardActivityResource',
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY]: 'BoardActivityCommentResource',
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT]: 'BoardActivityNestedCommentResource',
};

const PARENT_ID_PARAM_MAP = {
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD]: 'boardId',
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY]: 'activityId',
  [BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT]: 'activityId',
};

export type ActivityItemCreated = {
  type: 'ACTIVITY_ITEM_CREATED',
  payload: {
    parentId: string,
    activityItem: BoardActivityItem,
  },
};

export function activityItemCreated(
  parentId: string,
  activityItem: BoardActivityItem,
): ActivityItemCreated {
  return {
    type: 'ACTIVITY_ITEM_CREATED',
    payload: {
      parentId,
      activityItem,
    },
  };
}

export type ActivityItemCreateData = {
  contentMarkdown?: string,
  imageSignatures?: string,
  objectIds: Array<string>,
  parentId: string,
  parentType: BoardActivityItemType | 'board',
  text?: string,
  tags: Array<Object>,
  replyToCommentId?: ?string,
};

export function createActivityItem({
  contentMarkdown,
  imageSignatures,
  objectIds,
  parentId,
  parentType,
  text,
  tags,
  replyToCommentId,
}: ActivityItemCreateData): Thunk<Promise<Object>> {
  return (dispatch) => {
    const resourceName = PARENT_TO_CREATE_RESOURCE_MAP[parentType];
    const createData = {
      [PARENT_ID_PARAM_MAP[parentType]]: parentId,
      contentMarkdown,
      imageSignatures,
      objectIds,
      tags,
      text,
      replyToCommentId,
    };
    const feedType = PARENT_TO_FEED_TYPE[parentType];
    // NOTE(yansun): activity items (post and comments) are ordered new-to-oldest first-to-last except
    //   for nested comments (chilren of a comment activity item) which are ordered oldest-to-newest first-to-last
    //   the api for all activity item feeds will return items ordered new-to-oldest first-to-last
    const prepend = parentType !== BOARD_ACTIVITY_OBJECT_TYPES.BOARD_ACTIVITY_COMMENT;

    return ResourceFactory.create(resourceName, createData)
      .callCreate({ showError: true })
      .then((response: Object) => {
        const activityItem = response.resource_response.data;
        dispatch(activityItemCreated(parentId, activityItem));
        dispatch(
          itemsAdded({
            feedId: parentId,
            feedType,
            itemIds: [activityItem.id],
            itemType: activityItem.type,
            prepend,
          }),
        );
        dispatch(incrementCommentCount(parentId));
        return response;
      });
  };
}
